import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    computed,
    signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { PatientResponse } from '../../models/patient.model';
import { PatientsService, Transcription } from '../../services/patients.service';
import { applyChartPatientName } from '../../shared/utils/chart-patient-name.util';
import { synthesizeAssessmentSummary } from '../../shared/utils/soap-assessment-plan.util';
import {
    SUBJECTIVE_HEADING_SPECS,
    splitSoapByHeadings,
} from '../../shared/utils/soap-heading-split.util';
import { flattenSubjectiveStoryLabels } from '../../shared/utils/soap-section-format.util';
import { visitReasonFromSoap } from '../../shared/utils/visit-reason.util';
import {
    TranscriptionDetailService,
} from '../../transcriptions/transcription-details-service';
import { TranscriptionsService } from '../../transcriptions/transcriptions.service';
import { PatientModalComponent } from '../patient-modal/patient-modal.component';

interface MedicationRow {
  medication: string;
  dosage: string;
  frequency: string;
  startDate?: string;
  dateRange?: string;
  reasonForDiscontinuation?: string;
}

@Component({
  selector: 'app-patient-summary',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    PatientModalComponent
  ],
  templateUrl: './patient-summary.component.html',
  styleUrls: ['./patient-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.embedded]': 'embedded',
  },
})
export class PatientSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('patientModal') patientModal!: PatientModalComponent;
  /** When set (new shell), load this MRN/id instead of the route param. */
  @Input() patientKey: string | null = null;
  @Input() embedded = false;
  @Input() hideClinicalNotes = false;
  /** Medication History and Upcoming Appointments stay off until those sections are ready. */
  readonly showMedicationHistory = false;
  readonly showUpcomingAppointments = false;

  private destroy$ = new Subject<void>();
  private detailRequestId = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private patientsService: PatientsService,
    private transcriptionsService: TranscriptionsService,
    private transcriptionDetailService: TranscriptionDetailService
  ) {}

  patient = signal<PatientResponse | null>(null);
  loading = signal(true);
  editModalVisible = signal(false);

  // Transcription data
  transcriptions = signal<Transcription[]>([]);

  // Medications data
  currentMedications = signal<MedicationRow[]>([]);
  pastMedications = signal<MedicationRow[]>([]);
  readonly hasCurrentStartDates = computed(() =>
    this.currentMedications().some((med) => !!med.startDate)
  );

  // Clinical notes data
  clinicalNotes = signal<any[]>([]);

  // Appointments data
  upcomingAppointments = signal<any[]>([]);

  readonly visitNarrativeParagraphs = computed(() =>
    this.buildVisitNarrative(this.transcriptions())
  );

  private loadTranscriptionsData(): Observable<Transcription[]> {
    const patient = this.patient();
    if (!patient?.mrn) {
      return of([]).pipe(takeUntil(this.destroy$));
    }

    return this.patientsService.getTranscriptionsByPatientMrn(patient.mrn)
      .pipe(takeUntil(this.destroy$));
  }

  private loadClinicalNotesData(): Observable<any[]> {
    // For now, return empty array since we don't have a backend endpoint for clinical notes
    // This will be implemented when the backend provides this functionality
    return of([]).pipe(takeUntil(this.destroy$));
  }

  private visitTime(visit: Transcription): number {
    const raw = visit.appointmentTime || visit.date;
    const parsed = raw ? new Date(raw) : null;
    return parsed && Number.isFinite(parsed.getTime()) ? parsed.getTime() : 0;
  }

  private isFailedVisit(visit: Transcription): boolean {
    return (visit.status ?? '').toLowerCase().includes('fail');
  }

  private buildVisitNarrative(visits: Transcription[]): string[] {
    const usable = [...visits]
      .filter((v) => !this.isFailedVisit(v))
      .sort((a, b) => this.visitTime(b) - this.visitTime(a));
    const paragraphs: string[] = [];
    for (const visit of usable) {
      const paragraph = this.narrativeForVisit(visit);
      if (paragraph) {
        paragraphs.push(paragraph);
      }
    }
    return paragraphs;
  }

  private narrativeForVisit(visit: Transcription): string | null {
    const soap = visit.analysis?.soapNote;
    const dateLabel = this.formatDateLong(visit.date);
    if (dateLabel === 'N/A') {
      return null;
    }
    const reason =
      (visit.visitReason ?? '').trim() || visitReasonFromSoap(soap);
    const lead = reason
      ? `On ${dateLabel}, the patient was seen for ${reason}.`
      : `On ${dateLabel}, the patient was seen.`;

    const chartName = this.getPatientFullName();
    const aiSummary = this.aiAnalysisSummary(visit);
    if (aiSummary) {
      return `${lead} ${applyChartPatientName(aiSummary, chartName)}`;
    }

    const assessmentSummary = (soap?.assessmentSummary ?? '').trim();
    if (assessmentSummary) {
      return `${lead} ${applyChartPatientName(assessmentSummary, chartName)}`;
    }

    const synthesized = synthesizeAssessmentSummary(
      (soap?.problems ?? []).map((p) => ({ name: p.name ?? '' })),
      this.getPatientFullName()
    ).trim();
    if (synthesized) {
      return `${lead} ${synthesized}`;
    }

    const analysisSummary = (visit.analysis?.summary ?? '').trim();
    if (
      analysisSummary &&
      !/^error\b/i.test(analysisSummary) &&
      !/^medical analysis failed/i.test(analysisSummary)
    ) {
      return `${lead} ${applyChartPatientName(analysisSummary, chartName)}`;
    }

    const hpi = splitSoapByHeadings(soap?.subjective, SUBJECTIVE_HEADING_SPECS)
      .find((section) => section.id === 'hpi')?.body;
    const hpiFlat = flattenSubjectiveStoryLabels(hpi ?? '').replace(/\s+/g, ' ').trim();
    if (hpiFlat) {
      const clipped =
        hpiFlat.length > 800 ? `${hpiFlat.slice(0, 800).replace(/\s+\S*$/, '')}…` : hpiFlat;
      return `${lead} ${applyChartPatientName(clipped, chartName)}`;
    }

    return visit.soapNotesAvailable || reason ? lead : null;
  }

  private aiAnalysisSummary(visit: Transcription): string | null {
    const findings = (visit.analysis?.aiAnalysis?.keyFindings ?? [])
      .map((s) => (s ?? '').trim())
      .filter((s) => this.isUsableAiFinding(s));
    if (!findings.length) {
      return null;
    }
    return findings
      .map((s) => (/[.!?]$/.test(s) ? s : `${s}.`))
      .join(' ');
  }

  private isUsableAiFinding(text: string): boolean {
    if (!text) {
      return false;
    }
    if (/^\[ERROR\]/i.test(text) || /not available/i.test(text)) {
      return false;
    }
    if (/unable to generate/i.test(text) || /skipped due to/i.test(text)) {
      return false;
    }
    return true;
  }

  private applyMedicationsFromVisits(visits: Transcription[]): void {
    const usable = [...visits]
      .filter((v) => !this.isFailedVisit(v))
      .sort((a, b) => this.visitTime(b) - this.visitTime(a));

    const byVisit: { visit: Transcription; meds: MedicationRow[] }[] = [];
    for (const visit of usable) {
      const meds = this.medicationsFromVisit(visit);
      if (meds.length) {
        byVisit.push({ visit, meds });
      }
    }

    const current = byVisit[0]?.meds ?? [];
    const currentNames = new Set(current.map((m) => m.medication.toLowerCase()));
    const past: MedicationRow[] = [];
    const seenPast = new Set<string>();
    for (const { visit, meds } of byVisit.slice(1)) {
      for (const med of meds) {
        const key = med.medication.toLowerCase();
        if (currentNames.has(key) || seenPast.has(key)) {
          continue;
        }
        seenPast.add(key);
        past.push({
          ...med,
          dateRange: this.formatDate(visit.date),
          reasonForDiscontinuation: 'Not listed on most recent visit',
        });
      }
    }

    this.currentMedications.set(current);
    this.pastMedications.set(past);
  }

  private medicationsFromVisit(visit: Transcription): MedicationRow[] {
    const rows: MedicationRow[] = [];
    const seen = new Set<string>();

    const medsBody = splitSoapByHeadings(
      visit.analysis?.soapNote?.subjective,
      SUBJECTIVE_HEADING_SPECS
    ).find((section) => section.id === 'meds')?.body;
    if (medsBody) {
      for (const raw of medsBody.split('\n')) {
        const line = raw.replace(/^[-•*]\s+/, '').replace(/^\d+[.)]\s+/, '').trim();
        if (!line || /^(medications?|n\/a|none|none reported)\.?$/i.test(line)) {
          continue;
        }
        for (const piece of line.split(/\s*;\s*/)) {
          const parsed = this.parseMedicationLine(piece.trim());
          if (!parsed) {
            continue;
          }
          const key = parsed.medication.toLowerCase();
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          rows.push(parsed);
        }
      }
    }

    const rx = visit.analysis?.prescription;
    const rxName = rx?.medicationName?.trim();
    if (rxName && !seen.has(rxName.toLowerCase())) {
      rows.push({
        medication: rxName,
        dosage: rx?.dosage?.trim() || '',
        frequency: rx?.frequency?.trim() || '',
        startDate: this.extractMedicationStartDate(
          [rx?.instructions, rx?.duration].filter(Boolean).join(' ')
        ),
      });
    }
    return rows;
  }

  private parseMedicationLine(line: string): MedicationRow | null {
    const cleaned = line.replace(/\.$/, '').trim();
    if (!cleaned) {
      return null;
    }

    const startDate = this.extractMedicationStartDate(cleaned);
    const doseUnit = 'mg|mcg|µg|g|units?|iu|ml|meq|%';

    const ofDose = new RegExp(
      String.raw`(\d[\d./]*)\s*(${doseUnit})\s+of\s+([A-Za-z][A-Za-z0-9'/-]*)(?:\s*\(([^)]+)\))?`,
      'i'
    ).exec(cleaned);
    if (ofDose) {
      return {
        medication: this.formatMedName(ofDose[3], ofDose[4]),
        dosage: `${ofDose[1]} ${ofDose[2]}`,
        frequency: this.extractFrequency(cleaned),
        startDate,
      };
    }

    const structured = new RegExp(
      String.raw`^([A-Za-z][A-Za-z0-9'/-]*(?:\s+[A-Za-z][A-Za-z0-9'/-]*){0,3})(?:\s*\(([^)]+)\))?\s+(\d[\d./]*\s*(?:${doseUnit})\b.*)$`,
      'i'
    ).exec(cleaned);
    if (structured && this.looksLikeDrugName(structured[1])) {
      const split = this.splitDosageAndFrequency(structured[3].trim());
      return {
        medication: this.formatMedName(structured[1], structured[2]),
        dosage: split.dosage,
        frequency: split.frequency,
        startDate,
      };
    }

    const onMed =
      /\b(?:(?:has|have|had)\s+been\s+on|been\s+on|taking|prescribed|started(?:\s+on)?|currently\s+on)\s+([A-Za-z][A-Za-z0-9'/-]*(?:\s+[A-Za-z][A-Za-z0-9'/-]*){0,2})(?:\s*\(([^)]+)\))?/i.exec(
        cleaned
      );
    if (onMed && this.looksLikeDrugName(onMed[1])) {
      const doseInLine = new RegExp(String.raw`(\d[\d./]*\s*(?:${doseUnit})\b)`, 'i').exec(cleaned);
      return {
        medication: this.formatMedName(onMed[1], onMed[2]),
        dosage: doseInLine?.[1] ?? '',
        frequency: this.extractFrequency(cleaned),
        startDate,
      };
    }

    if (this.looksLikeStandaloneMedLine(cleaned)) {
      return {
        medication: cleaned,
        dosage: '',
        frequency: '',
        startDate,
      };
    }

    return null;
  }

  private formatMedName(name: string, brand?: string): string {
    const trimmed = name.trim();
    const brandName = brand?.trim();
    return brandName ? `${trimmed} (${brandName})` : trimmed;
  }

  private looksLikeDrugName(name: string): boolean {
    const trimmed = name.trim();
    if (!trimmed || trimmed.split(/\s+/).length > 4) {
      return false;
    }
    if (
      /^(the|a|an|she|he|they|her|his|their|there|this|that|it|we|i|patient|currently|also|then|when|after|before|due)\b/i.test(
        trimmed
      )
    ) {
      return false;
    }
    return !/\b(was|were|been|being|whether|given|patient|question|tapered)\b/i.test(trimmed);
  }

  private looksLikeStandaloneMedLine(text: string): boolean {
    if (text.length > 50 || /[,:;]/.test(text)) {
      return false;
    }
    if (/\b(was|were|has|have|had|is|are|may|given|being|whether|patient|question)\b/i.test(text)) {
      return false;
    }
    return this.looksLikeDrugName(text);
  }

  private extractMedicationStartDate(text: string | undefined): string | undefined {
    if (!text) {
      return undefined;
    }
    const labeled =
      /\b(?:started|start(?:ed)?(?:\s+date)?|since|beginning|as of)\s+(?:on\s+)?(\d{4}-\d{2}-\d{2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+\d{1,2},?\s+\d{4})\b/i.exec(
        text
      );
    return labeled?.[1];
  }

  private extractFrequency(text: string): string {
    const match =
      /\b(once|twice|three times|daily|bid|tid|qid|qhs|prn|q\d+h|every\s+\d+\s+(?:hours?|days?)|at bedtime|with meals|as needed)\b.*$/i.exec(
        text
      );
    return match?.[0]?.trim() ?? '';
  }

  private splitDosageAndFrequency(rest: string): { dosage: string; frequency: string } {
    const freqMatch =
      /\b(once|twice|three times|daily|bid|tid|qid|qhs|prn|q\d+h|every\s+\d+\s+(?:hours?|days?)|at bedtime|with meals|as needed)\b.*$/i.exec(
        rest
      );
    if (freqMatch && freqMatch.index > 0) {
      return {
        dosage: rest.slice(0, freqMatch.index).trim(),
        frequency: rest.slice(freqMatch.index).trim(),
      };
    }
    return { dosage: rest, frequency: '' };
  }

  ngOnInit(): void {
    if (this.embedded) {
      return;
    }
    this.loadPatientData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.embedded || !changes['patientKey']) {
      return;
    }
    const key = this.patientKey?.trim();
    if (!key) {
      this.patient.set(null);
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.patient.set(null);
    this.transcriptions.set([]);
    this.currentMedications.set([]);
    this.pastMedications.set([]);
    this.clinicalNotes.set([]);
    this.loadPatientByKey(key);
  }

  private loadPatientData(): void {
    const patientId = this.route.snapshot.paramMap.get('id');
    if (patientId) {
      this.loadPatientByKey(patientId);
    } else {
      this.loading.set(false);
    }
  }

  private loadPatientByKey(patientId: string): void {
    const requestId = ++this.detailRequestId;
    this.patientsService.getPatientByMRN(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (patient) => {
          if (requestId !== this.detailRequestId) {
            return;
          }
          if (patient) {
            this.applyLoadedPatient(patient, patientId);
            return;
          }
          const numericId = parseInt(patientId, 10);
          if (!Number.isFinite(numericId)) {
            this.loading.set(false);
            return;
          }
          this.patientsService.getPatientById(numericId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (byId) => {
                if (requestId !== this.detailRequestId) {
                  return;
                }
                this.applyLoadedPatient(byId, patientId);
              },
              error: (error) => {
                if (requestId !== this.detailRequestId) {
                  return;
                }
                console.error('Error loading patient by ID:', error);
                this.loading.set(false);
              }
            });
        },
        error: (error) => {
          if (requestId !== this.detailRequestId) {
            return;
          }
          console.error('Error loading patient by MRN:', error);
          this.loading.set(false);
        }
      });
  }

  private applyLoadedPatient(patient: PatientResponse, patientId: string): void {
    this.patient.set(patient);
    this.loadTranscriptions(patientId);
    if (!this.hideClinicalNotes) {
      this.loadClinicalNotes(patientId);
    }
    this.loading.set(false);
  }

  private loadTranscriptions(patientId: string): void {
    this.loadTranscriptionsData()
      .pipe(
        switchMap((rows) => this.hydrateVisitAnalysis(rows)),
        takeUntil(this.destroy$)
      )
      .subscribe((transcriptions) => {
        this.transcriptions.set(transcriptions);
        this.applyMedicationsFromVisits(transcriptions);
      });
  }

  /** Fill SOAP/analysis when the patient list payload omitted it (older API or failed attach). */
  private hydrateVisitAnalysis(rows: Transcription[]): Observable<Transcription[]> {
    const missingSoap = rows.filter((row) => row.soapNotesAvailable && !row.analysis?.soapNote);
    const afterSoap$ = missingSoap.length
      ? forkJoin(
          missingSoap.map((row) =>
            this.transcriptionsService.getTranscriptionById(row.id).pipe(
              map((detail) => ({ id: row.id, detail })),
              catchError(() => of({ id: row.id, detail: null }))
            )
          )
        ).pipe(
          map((loaded) => {
            const byId = new Map(loaded.map((item) => [item.id, item.detail]));
            return rows.map((row) => {
              const detail = byId.get(row.id);
              if (!detail?.analysis) {
                return row;
              }
              return {
                ...row,
                analysis: detail.analysis,
                visitReason: row.visitReason || detail.visitReason,
              };
            });
          })
        )
      : of(rows);

    return afterSoap$.pipe(switchMap((withSoap) => this.hydrateMissingAiAnalysis(withSoap)));
  }

  private hydrateMissingAiAnalysis(rows: Transcription[]): Observable<Transcription[]> {
    const missing = rows.filter(
      (row) => row.soapNotesAvailable && !this.isFailedVisit(row) && !this.aiAnalysisSummary(row)
    );
    if (!missing.length) {
      return of(rows);
    }
    return forkJoin(
      missing.map((row) =>
        this.transcriptionDetailService.loadAIAnalysis(row.id).pipe(
          map((content) => ({ id: row.id, content })),
          catchError(() => of({ id: row.id, content: null }))
        )
      )
    ).pipe(
      map((loaded) => {
        const byId = new Map(loaded.map((item) => [item.id, item.content]));
        return rows.map((row) => {
          const content = byId.get(row.id);
          const findings = (content?.keyFindings ?? [])
            .map((s) => (s ?? '').trim())
            .filter((s) => this.isUsableAiFinding(s));
          if (!findings.length) {
            return row;
          }
          return {
            ...row,
            analysis: {
              ...(row.analysis ?? { id: '' }),
              aiAnalysis: {
                keyFindings: findings,
                treatmentPlanSummary: content?.treatmentPlanSummary,
              },
            },
          };
        });
      })
    );
  }

  private loadClinicalNotes(patientId: string): void {
    this.loadClinicalNotesData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(notes => {
        // Sort clinical notes by date (newest first)
        const sortedNotes = notes.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime(); // Descending order
        });
        
        this.clinicalNotes.set(sortedNotes);
      });
  }

  onNewVisit(): void {
    const patient = this.patient();
    if (patient?.id == null) {
      return;
    }
    const path = this.embedded ? ['/recording'] : ['/v0/recording'];
    void this.router.navigate(path, {
      queryParams: { patientId: patient.id, autoStart: '1' },
    });
  }

  onEditPatient(): void {
    this.editModalVisible.set(true);
    // Force form population after a short delay
    setTimeout(() => {
      if (this.patientModal) {
        this.patientModal.populateForm();
      }
    }, 100);
  }

  onPatientUpdated(updatedPatient: any): void {
    this.patient.set(updatedPatient);
    
    // Force reload of patient data to ensure we have the latest data
    this.loadPatientData();
  }

  onExport(): void {
    const patient = this.patient();
    if (!patient) {
      return;
    }
    const payload = this.buildChartPayload();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patient-${patient.mrn || patient.id}-export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onPrint(): void {
    document.body.classList.add('print-patient-chart');
    const cleanup = () => document.body.classList.remove('print-patient-chart');
    window.addEventListener('afterprint', cleanup, { once: true });
    // Safari / some webviews skip afterprint if print is cancelled; always clear.
    window.setTimeout(cleanup, 2000);
    window.print();
  }

  private buildChartPayload(): Record<string, unknown> {
    const patient = this.patient();
    return {
      exportedAt: new Date().toISOString(),
      patient: patient
        ? {
            id: patient.id,
            mrn: patient.mrn,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth,
            age: this.calculateAge(patient.dateOfBirth),
            sex: patient.sex,
            phoneNumber: patient.phoneNumber,
            email: patient.email,
            streetAddress: patient.streetAddress,
            city: patient.city,
            state: patient.state,
            zipCode: patient.zipCode,
            allergies: patient.allergies,
            primaryPhysician: patient.primaryPhysician,
            insuranceName: patient.insuranceName,
            insurancePolicyNumber: patient.insurancePolicyNumber,
            insurancePhone: patient.insurancePhone,
            active: patient.active,
            lastVisit: this.getLastVisitDate(),
          }
        : null,
      summary: this.visitNarrativeParagraphs(),
      transcriptions: this.transcriptions().map((t) => ({
        id: t.id,
        date: t.date,
        duration: t.duration,
        status: t.status,
        visitReason: t.visitReason,
      })),
      currentMedications: this.currentMedications(),
      pastMedications: this.pastMedications(),
      upcomingAppointments: this.upcomingAppointments(),
    };
  }

  onViewTranscription(transcriptionId: string | undefined): void {
    if (!transcriptionId) {
      console.error('Cannot navigate: transcriptionId is undefined');
      return;
    }
    if (this.embedded) {
      this.transcriptionsService.requestSelectVisitOnLoad(transcriptionId);
      void this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/v0/transcriptions', transcriptionId]);
  }

  onViewClinicalNote(noteId: string): void {
    // Navigate to clinical note view
    this.router.navigate(['/clinical-notes', noteId]);
  }

  getPatientFullName(): string {
    const patient = this.patient();
    return patient ? `${patient.firstName} ${patient.lastName}` : '';
  }

  getPatientInitials(): string {
    const patient = this.patient();
    if (!patient) return '';
    return `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  calculateAge(dateOfBirth: string | undefined): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      // Parse date string (format: YYYY-MM-DD) as local date to avoid timezone issues
      // Split the date string and create a date object directly to prevent UTC conversion
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);
        const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
        const dayStr = date.getDate().toString().padStart(2, '0');
        const yearStr = date.getFullYear();
        return `${monthStr}-${dayStr}-${yearStr}`;
      } else {
        // Fallback to original parsing if format is different
        const date = new Date(dateString);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      }
    } catch (error) {
      return dateString;
    }
  }

  formatDateLong(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      // Parse date string (format: YYYY-MM-DD) as local date to avoid timezone issues
      // Split the date string and create a date object directly to prevent UTC conversion
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } else {
        // Fallback to original parsing if format is different
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      return dateString;
    }
  }

  getLastVisitDate(): string {
    const transcriptions = this.transcriptions();
    if (!transcriptions || transcriptions.length === 0) {
      return 'No visits yet';
    }
    const newest = [...transcriptions].sort((a, b) => this.visitTime(b) - this.visitTime(a))[0];
    return this.formatDateLong(newest.date);
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Needs Review':
        return 'warning';
      case 'In Progress':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getPatientStatus(): string {
    const patient = this.patient();
    if (!patient) return 'Unknown';
    return patient.active ? 'Active' : 'Inactive';
  }
}
